// externals
import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useMemo, useRef, useState } from 'react';

// selectors
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

// components
import { EditedTaxFieldInputContainer } from './EditedTaxField.style';

// actions
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { TextInputEventValue } from '@interstate/components/TextInput';
import type { TaxTotalAmountFieldProps } from '../types';
import { getHumanReadableTaxName, useEditableProp } from './helpers';
import { PriceInput } from '@interstate/components/PriceInput';
import { createSelectOnFocusHandler, useSelectOnFocusEnabled } from '@makemydeal/dr-shared-components/select-on-focus';
import { formatNumberDecimals } from '@makemydeal/dr-common-utils';
import { convertToNumber } from '../../../utils/formatUtils';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import * as dealScreenErrorSelectors from '@makemydeal/dr-dash-store/selectors/dealScreenErrorSelectors';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import { dealLockHooks } from '@makemydeal/dr-dash-components';
import { DealTabs } from '@makemydeal/dr-shared-types';

// consts/enums
import { TAX_ITEM_TOTAL_THRESHOLD, TAXES_NUMERIC_MAX_VALUE_MESSAGE, taxNameMappingTable } from '../constants';

const InputLabelStyle = {
    width: '9rem',
    '.ids-input-component-label': {
        justifyContent: 'end !important'
    }
};

export default function TaxItemTotalField<Props extends TaxTotalAmountFieldProps>({
    data,
    handleBlur,
    shouldDisableInput = false
}: Props) {
    const formattedAmount = formatNumberDecimals(data.taxAmount);
    const [taxAmount, setTaxAmount, isEdited] = useEditableProp(formattedAmount, data.manualChanges?.taxAmount);
    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);
    const isAutoSelectEnabled = useSelectOnFocusEnabled(featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap);
    const isLocked = dealLockHooks.useIsDealLocked();
    const dispatch = useDispatch();
    const prevValueBeforeBlur = useRef<string>(taxAmount || '');
    const isBlurInProgress = useRef(false);
    const fieldName = useMemo(() => {
        return getHumanReadableTaxName(data.taxName, taxNameMappingTable);
    }, [getHumanReadableTaxName]);
    const [hasError, setHasError] = useState(false);

    const isTaxTotalValid = useSelector(dealScreenErrorSelectors.getFieldValidationError(DealTabs.TAXES, fieldName));

    useEffect(() => {
        if (shouldDisableInput && isTaxTotalValid) {
            dispatch(dealScreenErrorActionCreators.setFieldError(DealTabs.TAXES, fieldName, false));
        }
    }, [shouldDisableInput, isTaxTotalValid]);

    const setValidAmount = () => {
        if (!taxAmount && !data.taxAmount) {
            setTaxAmount('0');
        }
    };

    const handleBlurWrapper = (e: InterstateOnChangeEvent<TextInputEventValue>) => {
        isBlurInProgress.current = true;
        setValidAmount();
        prevValueBeforeBlur.current = e.target.value;
        handleBlur(e);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleBlurWrapper({ target: { value: e.currentTarget.value } });
            prevValueBeforeBlur.current = e.currentTarget.value;
        }
    };

    const handleChange = (e: InterstateOnChangeEvent<TextInputEventValue>) => {
        const hasErrorInAmountField = convertToNumber(e.target.value) > TAX_ITEM_TOTAL_THRESHOLD;
        setHasError(hasErrorInAmountField);
        if (isBlurInProgress.current) {
            isBlurInProgress.current = false;
            return;
        }
        dispatch(
            offerActionCreators.updatePaymentCallPendingState(
                convertToNumber(e.target.value) !== convertToNumber(prevValueBeforeBlur.current)
            )
        );

        if (isDealScreenRedDotErrorsEnabled && isTaxTotalValid !== hasErrorInAmountField)
            dispatch(dealScreenErrorActionCreators.setFieldError(DealTabs.TAXES, fieldName, hasErrorInAmountField));

        setTaxAmount(e.target.value);
    };

    return (
        <EditedTaxFieldInputContainer $isEdited={isEdited}>
            <PriceInput
                sx={InputLabelStyle}
                id="tax-item-tax-total"
                data-testid="tax-item-tax-total"
                name={'detail-total'}
                label="Tax Total"
                onChange={handleChange}
                value={taxAmount || 0}
                onBlur={handleBlurWrapper}
                onFocus={createSelectOnFocusHandler(isAutoSelectEnabled)}
                onKeyDown={handleKeyDown}
                allowDecimal={true}
                decimalMaxLength={2}
                allowNegative={false}
                autoInsertCommas={true}
                textAlign="right"
                disabled={isLocked || shouldDisableInput}
                size="sm"
                hasError={hasError}
                errorMessage={TAXES_NUMERIC_MAX_VALUE_MESSAGE}
            />
        </EditedTaxFieldInputContainer>
    );
}
